import { useEffect, useRef, useState } from 'react';

import { resolvePersistedPeriodValue } from './periodPayload';
import { CycleInformationFormFields, type CycleInformationFormData } from './validation';

import type { SelectedFilters } from '@/hooks/filters/useCompanyAttributesFilters';
import type { CycleEvaluationGroup } from '@/types/cycle-evaluation/CycleEvaluationGroup';
import type { Company } from '@/types/directory/Company';
import type { UseFormReset, UseFormSetValue } from 'react-hook-form';

import { CycleAudienceType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { getCompanyAttributeList, type CompanyAttributeMap } from '@/utils/company';
import { transformCriteriaToFilters } from '@/utils/cycleEvaluation';

interface UseCycleInformationFormSyncParams {
    isNewCycle: boolean;
    group?: CycleEvaluationGroup;
    companyInformation?: Company;
    resolvedEmployeeCount: number;
    reset: UseFormReset<CycleInformationFormData>;
    setValue: UseFormSetValue<CycleInformationFormData>;
    setFilters: (filters: SelectedFilters) => void;
    setSelectedCompanyAttributes: (attributes: CompanyAttributeMap) => void;
}

export const useCycleInformationFormSync = ({
    isNewCycle,
    group,
    companyInformation,
    resolvedEmployeeCount,
    reset,
    setValue,
    setFilters,
    setSelectedCompanyAttributes,
}: UseCycleInformationFormSyncParams) => {
    const [isAudienceHydrated, setIsAudienceHydrated] = useState(isNewCycle);

    const hydratedRef = useRef(false);

    useEffect(() => {
        if (!group || hydratedRef.current) {
            return;
        }

        hydratedRef.current = true;

        reset({
            [CycleInformationFormFields.name]: group.name,
            [CycleInformationFormFields.selectedPeriod]: resolvePersistedPeriodValue(group),
            [CycleInformationFormFields.periodStart]: group.evaluationPeriodStart
                ? new Date(group.evaluationPeriodStart)
                : undefined,
            [CycleInformationFormFields.periodEnd]: group.evaluationPeriodEnd
                ? new Date(group.evaluationPeriodEnd)
                : undefined,

            [CycleInformationFormFields.audienceType]:
                group.audience?.cycleAudienceType ?? CycleAudienceType.ENTIRE_COMPANY,
            [CycleInformationFormFields.resolvedEmployeeCount]: 0,
        });
    }, [group, reset]);

    const audienceFiltersHydratedRef = useRef(false);

    useEffect(() => {
        if (audienceFiltersHydratedRef.current) {
            return;
        }

        if (!isNewCycle && !group) {
            return;
        }

        const persistedCriterias = group?.audience?.cycleAudienceCriterias ?? [];

        if (persistedCriterias.length > 0) {
            if (!companyInformation) {
                return;
            }

            const { filters: restoredFilters, selectedAttributes } = transformCriteriaToFilters({
                criterias: persistedCriterias,
                companyAttributes: getCompanyAttributeList(companyInformation),
            });

            setFilters(restoredFilters);
            setSelectedCompanyAttributes(selectedAttributes);
        }

        audienceFiltersHydratedRef.current = true;

        setIsAudienceHydrated(true);
    }, [isNewCycle, group, companyInformation, setFilters, setSelectedCompanyAttributes]);

    useEffect(() => {
        setValue(CycleInformationFormFields.resolvedEmployeeCount, resolvedEmployeeCount);
    }, [resolvedEmployeeCount, setValue]);

    return { isAudienceHydrated };
};
